/**
 * Mini Player Component - Jerusalem Gold Design
 *
 * Elegant mini player with warm glass effect and gold accents.
 * Shows current playing track, tappable to expand.
 */

import { usePlayerStore } from '@/stores/player';
import { PlayIcon, PauseIcon, SkipNextIcon } from '../icons';

export function MiniPlayer() {
  const { currentSong, isPlaying, progress, duration, toggle, skipNext, setExpanded } = usePlayerStore();

  // Don't render if nothing is playing
  if (!currentSong) return null;

  // Get thumbnail URL - support both array and direct string formats
  const thumbnail = currentSong.thumbnail || currentSong.thumbnails?.[0]?.url || './pwa-192x192.webp';

  return (
    <div
      className="fixed left-0 right-0 h-player-mini z-player
                 bottom-16 md:bottom-0
                 flex items-center px-4 gap-3 cursor-pointer
                 transition-all duration-300 active:scale-[0.995]
                 player-mini"
      onClick={() => setExpanded(true)}
    >
      {/* Progress indicator line */}
      <div className="absolute top-0 left-0 right-0 h-0.5 bg-outline-variant/20">
        <div
          className="h-full bg-primary transition-all duration-100"
          style={{ width: duration > 0 ? `${(progress / duration) * 100}%` : '0%' }}
        />
      </div>

      {/* Thumbnail with warm shadow */}
      <div className="relative shrink-0">
        <img
          src={thumbnail}
          alt={currentSong.title}
          className="w-12 h-12 rounded-lg object-cover"
          style={{ boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)' }}
          referrerPolicy="no-referrer"
          crossOrigin="anonymous"
          onError={(e) => {
            (e.target as HTMLImageElement).src = './pwa-192x192.webp';
          }}
        />
        {/* Playing indicator */}
        {isPlaying && (
          <div className="absolute -bottom-1 -right-1 w-3 h-3 rounded-full bg-primary animate-glow-pulse" />
        )}
      </div>

      {/* Song info */}
      <div className="flex-1 min-w-0">
        <p className="font-medium text-on-surface line-clamp-1">
          {currentSong.title}
        </p>
        <p className="text-sm text-on-surface-variant line-clamp-1">
          {currentSong.artists?.map((a) => a.name).join(', ')}
        </p>
      </div>

      {/* Controls */}
      <div className="flex items-center gap-1 shrink-0">
        {/* Play/Pause */}
        <button
          className="w-11 h-11 rounded-full flex items-center justify-center
                     bg-primary text-on-primary
                     transition-all duration-200 hover:scale-105 active:scale-95"
          style={{ boxShadow: '0 4px 12px rgba(212, 165, 116, 0.3)' }}
          onClick={(e) => {
            e.stopPropagation();
            toggle();
          }}
          aria-label={isPlaying ? 'Pause' : 'Play'}
        >
          {isPlaying ? (
            <PauseIcon className="w-5 h-5" />
          ) : (
            <PlayIcon className="w-5 h-5 ml-0.5" />
          )}
        </button>

        {/* Next - hidden on mobile */}
        <button
          className="btn-icon hidden sm:flex text-on-surface-variant hover:text-on-surface"
          onClick={(e) => {
            e.stopPropagation();
            skipNext();
          }}
          aria-label="Next"
        >
          <SkipNextIcon className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}
